import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Tv, Copy, Check, Cast, MonitorPlay, X } from 'lucide-react';

import { Btn } from './Btn';
import { castService, isAppleMobile, type CastStatus } from '../../services/castService';

/**
 * THE ONE PLACE CASTING IS EXPLAINED.
 *
 * Three paths, and the modal offers exactly the ones that can actually work in
 * the browser it is running in:
 *
 *   1. CAST TO TV — a real Google Cast device picker. Chrome desktop and
 *      Android Chrome only, and only once a receiver App ID is registered.
 *   2. THE QR / URL — works on every television, laptop and tablet with a
 *      browser. This is the path most families will use, and it is not a
 *      consolation prize: it is the one that works everywhere.
 *   3. USE THIS SCREEN — the laptop already open on the coffee table becomes
 *      the table display. Costs nothing, and it is the fastest way for a host
 *      to find out what TV mode even is.
 *
 * WHAT THIS MODAL WILL NOT DO: show a Cast button on an iPhone. iOS cannot cast
 * a web page — Apple does not expose the Cast API to Safari, and every iOS
 * browser is Safari underneath. A button that opens an empty picker, or one
 * that quietly does nothing, is worse than no button at all: it makes the user
 * believe their television is broken. So we say the true thing instead.
 */
export function CastModal({ roomCode, onClose }: { roomCode: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [cast, setCast] = useState<CastStatus>(castService.getStatus());
  const [casting, setCasting] = useState(false);

  const tvUrl = `${window.location.origin}/tv/${roomCode}`;
  const cleanUrl = tvUrl.replace(/^https?:\/\//, '');
  const apple = isAppleMobile();

  useEffect(() => {
    const unsub = castService.subscribe(setCast);
    // Kick the SDK. On a platform that cannot cast this is a no-op that resolves
    // to `unsupported` without fetching a single byte of Google's JavaScript.
    void castService.init();
    return unsub;
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tvUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied — the URL is on screen anyway */ }
  };

  const handleCast = async () => {
    setCasting(true);
    try {
      await castService.castRoom(roomCode);
    } finally {
      setCasting(false);
    }
  };

  const canCast = cast.availability === 'available' || cast.availability === 'connected';
  const connected = cast.availability === 'connected';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Put the game on the TV"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="card lg"
        style={{ width: '100%', maxWidth: 400, padding: 24, background: 'var(--surface)', position: 'relative' }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: 'none',
            background: 'var(--bg-2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={16} color="var(--ink-3)" />
        </button>

        <div className="wr-col wr-center gap-2 mt-1">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tv size={24} strokeWidth={2.4} />
          </div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22, marginTop: 4, textAlign: 'center' }}>
            Put the game on the TV
          </div>
          <div className="wr-text-3" style={{ fontSize: 13, textAlign: 'center', lineHeight: 1.45 }}>
            The TV shows the letter, the clock and the tally.<br />
            Your answers stay on your phone.
          </div>
        </div>

        {/* ── Path 1: real Google Cast, offered ONLY where it really works ── */}
        {canCast && (
          <div className="mt-4">
            <Btn
              kind={connected ? 'ghost' : 'primary'}
              full
              size="lg"
              loading={casting}
              onClick={connected ? () => castService.stop() : handleCast}
            >
              <span className="wr-row gap-2" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Cast size={18} strokeWidth={2.4} />
                {connected ? `Stop casting to ${cast.deviceName}` : 'Cast to TV'}
              </span>
            </Btn>
            {connected && (
              <div
                className="wr-row gap-2 mt-2"
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  color: 'var(--green, #1f8f4a)',
                  fontWeight: 600,
                }}
              >
                <Check size={14} strokeWidth={3} />
                Showing room {roomCode} on {cast.deviceName}
              </div>
            )}
          </div>
        )}

        {/* ── The honest sentence, for every browser that cannot cast ─────── */}
        {!canCast && (
          <div className="card mt-4" style={{ padding: '12px 14px', background: 'var(--bg-2)', boxShadow: 'none' }}>
            <div className="wr-row gap-2" style={{ alignItems: 'flex-start' }}>
              <Cast size={16} color="var(--ink-3)" style={{ marginTop: 2, flexShrink: 0 }} />
              <div className="wr-text-3" style={{ fontSize: 12, lineHeight: 1.5 }}>
                {apple
                  ? 'iPhones can’t cast a web page — Apple doesn’t allow it. Open the link below on your TV instead. It takes about ten seconds.'
                  : cast.availability === 'no-devices'
                    ? 'No Chromecast found on this network. The link below works on any TV browser.'
                    : cast.availability === 'unconfigured'
                      ? 'One-tap casting isn’t switched on yet. The link below works on any TV, today.'
                      : 'This browser can’t cast a web page. The link below works on any TV, laptop or tablet.'}
              </div>
            </div>
          </div>
        )}

        {/* ── Path 2: the QR. Works on literally every television. ────────── */}
        <div
          className="wr-col wr-center mt-3"
          style={{ background: '#fff', borderRadius: 16, padding: 16, border: '1px solid var(--line)' }}
        >
          <QRCodeSVG value={tvUrl} size={200} level="M" bgColor="#ffffff" fgColor="#16161a" />
        </div>

        <div className="wr-col gap-2 mt-3">
          <div className="eyebrow" style={{ paddingLeft: 4 }}>
            Open this on any TV, laptop or tablet
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              background: 'var(--bg-2)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ink-1)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {cleanUrl}
              </div>
              <div className="wr-text-3" style={{ fontSize: 11 }}>
                {copied ? 'Copied — paste it into the TV’s browser' : 'Tap to copy'}
              </div>
            </div>
            {copied ? <Check size={14} color="var(--green, #1f8f4a)" /> : <Copy size={14} color="var(--ink-3)" />}
          </button>
        </div>

        {/* ── Path 3: this very screen becomes the table display. ─────────── */}
        <div className="mt-3">
          <Btn kind="ghost" full onClick={() => window.open(tvUrl, '_blank', 'noopener')}>
            <span className="wr-row gap-2" style={{ alignItems: 'center', justifyContent: 'center' }}>
              <MonitorPlay size={16} strokeWidth={2.2} />
              Use this screen as the table display
            </span>
          </Btn>
        </div>

        <div className="wr-text-3 mt-3" style={{ fontSize: 11, lineHeight: 1.5, textAlign: 'center' }}>
          Once the TV is on, every phone in the room becomes a controller.
        </div>
      </div>
    </div>
  );
}
